/**
 * EMBED-ROUTE — the path an embedded app reports to its host (`AI_APP_ROUTE { generation, path }`) and that the host hands back as `?path=` when it
 * frames the app again. THE SAME check as the host's (`apps/vitto-app/src/embed/app-route.ts`): a relative address inside the app's own origin,
 * nothing else — begins with `/`, not `//`, no backslash or control character, ≤ 2048, never a scheme or another origin, and without the host
 * protocol's own query keys. The app repeats it before it navigates: it trusts neither the wire nor the storage in between.
 */
export const MAX_APP_PATH = 2048;

/** Query keys of the host protocol: a reported or restored path never carries them. */
export const HOST_QUERY_KEYS = ['embedded', 'parentOrigin', 'workspace', 'space', 'path'] as const;

export function validAppPath(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_APP_PATH) return null;
  if (value.charCodeAt(0) !== 0x2f || value.charCodeAt(1) === 0x2f) return null;
  for (let i = 0; i < value.length; i += 1) {
    const c = value.charCodeAt(i);
    if (c <= 0x1f || c === 0x7f || c === 0x5c) return null; // control characters and `\`
  }
  try {
    const url = new URL(value, 'https://app.invalid');
    if (url.origin !== 'https://app.invalid') return null;
    // Canonical form (GABO C1): `new URL` resolves dot segments, so `/.//evil.example` keeps the origin but its RESOLVED pathname is
    // `//evil.example` (a replaceState to it throws in a browser). What the app navigates to is what was checked, byte for byte.
    if (`${url.pathname}${url.search}${url.hash}` !== value) return null;
    if (HOST_QUERY_KEYS.some((key) => url.searchParams.has(key))) return null;
  } catch {
    return null;
  }
  return value;
}

/** What to REPORT for a location: its path and query without the host's own keys (a document framed by Vitto carries them in its URL). */
export function reportablePath(location: { pathname: string; search: string }): string | null {
  const params = new URLSearchParams(location.search);
  for (const key of HOST_QUERY_KEYS) params.delete(key);
  const query = params.toString();
  return validAppPath(`${location.pathname}${query === '' ? '' : `?${query}`}`);
}
